import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const stats = [
  { val: '500+', label: 'Clients Treated' },
  { val: '8+', label: 'Years Experience' },
  { val: '15+', label: 'Premium Treatments' },
  { val: '98%', label: 'Client Satisfaction' },
];

const About = () => {
  return (
    <Section id="about">
      <Inner>
        <LeftCol>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label>Who We Are</Label>
            <Title>Beauty is a<br /><em>Science.</em><br />We speak it<br />fluently.</Title>
          </motion.div>

          <StatsRow>
            {stats.map((s, i) => (
              <Stat
                key={s.label}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <StatVal>{s.val}</StatVal>
                <StatLabel>{s.label}</StatLabel>
              </Stat>
            ))}
          </StatsRow>
        </LeftCol>

        <RightCol>
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <ImagePlaceholder>
              <PlaceholderInner>
                <PlaceholderIcon>✦</PlaceholderIcon>
                <PlaceholderText>Studio Photography<br />Coming Soon</PlaceholderText>
              </PlaceholderInner>
              <ImageAccent />
            </ImagePlaceholder>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <TextBlock>
              <Para>
                Elaia Aesthetic was born from a singular belief: that every person deserves access to medical-grade skincare expertise delivered with warmth, precision, and genuine care.
              </Para>
              <Para>
                Our clinic combines the latest in aesthetic technology — from the Zemits DermeLuxx Pro hydrodermabrasion system to the Dr. Pen A11 Ultima PRO microneedling device — with deeply individualized treatment protocols.
              </Para>
              <Para>
                We don't do one-size-fits-all. We do <em>you</em>.
              </Para>
            </TextBlock>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.35 }}
          >
            <PhilosophyCard>
              <PhilosophyQ>"</PhilosophyQ>
              <PhilosophyText>
                Real results. No compromises. Your skin deserves the very best science can offer.
              </PhilosophyText>
              <PhilosophyAuthor>— Elaia, Founder</PhilosophyAuthor>
            </PhilosophyCard>
          </motion.div>
        </RightCol>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  background: var(--charcoal-soft);
  padding: 140px 0;
  position: relative;
`;
const Inner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 60px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 100px;
  align-items: start;
  @media (max-width: 1024px) { grid-template-columns: 1fr; gap: 60px; }
  @media (max-width: 768px) { padding: 0 24px; }
`;
const LeftCol = styled.div`
  position: sticky;
  top: 120px;
  @media (max-width: 1024px) { position: relative; top: auto; }
`;
const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;
const Label = styled.span`
  display: block;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 28px;
`;
const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(44px, 5.5vw, 80px);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 0 0 64px;
  em { font-style: italic; color: var(--rose); }
`;
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
`;
const Stat = styled.div`
  border-top: 1px solid rgba(196, 149, 122, 0.2);
  padding-top: 20px;
`;
const StatVal = styled.div`
  font-family: var(--font-display);
  font-size: clamp(36px, 4vw, 52px);
  font-weight: 300;
  color: var(--rose);
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 8px;
`;
const StatLabel = styled.div`
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--taupe);
`;
const ImagePlaceholder = styled.div`
  position: relative;
  width: 100%;
  height: 420px;
  background: rgba(44, 40, 37, 0.6);
  border: 1px solid rgba(196, 149, 122, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PlaceholderInner = styled.div`
  text-align: center;
`;
const PlaceholderIcon = styled.div`
  font-size: 32px;
  color: var(--rose);
  opacity: 0.4;
  margin-bottom: 16px;
`;
const PlaceholderText = styled.p`
  font-family: var(--font-body);
  font-size: 12px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--taupe);
  opacity: 0.5;
  line-height: 2;
`;
const ImageAccent = styled.div`
  position: absolute;
  bottom: -16px;
  right: -16px;
  width: 120px;
  height: 120px;
  border: 1px solid rgba(196, 149, 122, 0.25);
  pointer-events: none;
`;
const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const Para = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 300;
  color: var(--taupe);
  line-height: 1.85;
  margin: 0;
  em { font-style: italic; color: var(--cream); }
`;
const PhilosophyCard = styled.div`
  border-left: 2px solid var(--rose);
  padding: 24px 32px;
  background: rgba(196, 149, 122, 0.04);
`;
const PhilosophyQ = styled.span`
  display: block;
  font-family: var(--font-display);
  font-size: 48px;
  color: var(--rose);
  line-height: 0.5;
  margin-bottom: 16px;
  opacity: 0.6;
`;
const PhilosophyText = styled.p`
  font-family: var(--font-display);
  font-size: clamp(18px, 2vw, 22px);
  font-style: italic;
  font-weight: 300;
  color: var(--cream);
  line-height: 1.6;
  margin: 0 0 16px;
`;
const PhilosophyAuthor = styled.span`
  font-family: var(--font-body);
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--rose);
`;

export default About;
